import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";

type Product = { title: string; commissionRate: number | null; imageUrl: string | null };

type Props = {
  products: Product[];
  groupTooltip?: boolean;
  visible?: number;
};

const formatRate = (rate: number | null) => {
  if (rate === null || rate === undefined) return "";
  // commissionRate is stored as a decimal fraction (0.12 = 12%), matching the real
  // TikTok-sync convention (collaborations/repo.ts: commissionRate = commission.rate / 10000).
  return `${(rate * 100).toFixed(0)}%`;
};

const Chip = ({ product }: { product: Product }) => (
  <span className="inline-flex items-center gap-1 py-0.5 rounded bg-gray-50 text-sm font-medium text-secondary whitespace-nowrap">
    {product.imageUrl && (
      <img src={product.imageUrl} alt="" className="w-5 h-5 rounded-sm object-cover flex-shrink-0" />
    )}
    <span className="truncate max-w-[10ch]">{product.title}</span>
    {product.commissionRate !== null && <span className="text-tertiary">{formatRate(product.commissionRate)}</span>}
  </span>
);

export const ProductChips = ({ products, groupTooltip, visible = 2 }: Props) => {
  if (!products || products.length === 0) return null;

  const shown = products.slice(0, visible);
  const overflow = products.length - shown.length;

  const overflowChip =
    overflow > 0 ? (
      <Tooltip
        title={`${products.length} products`}
        description={
          groupTooltip
            ? "These are grouped to help manage similar collaborations at once. Products are shared across the group's collaborations."
            : products
                .slice(visible)
                .map((p) => `${p.title} ${formatRate(p.commissionRate)}`)
                .join(", ")
        }
        placement="top"
      >
        <TooltipTrigger className="inline-flex">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-[10px] font-medium text-tertiary">
            +{overflow}
          </span>
        </TooltipTrigger>
      </Tooltip>
    ) : null;

  return (
    <div className="flex flex-wrap items-center gap-2.5 mt-0.5">
      {shown.map((p) => (
        <Chip key={p.title} product={p} />
      ))}
      {overflowChip}
    </div>
  );
};
